import { FC, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  WORK_ORDER_ABBR_STATUS_COLOR_MAP,
  WORK_ORDER_HOVER_STATUS_MAP,
  WORK_ORDER_STATUSES_LABELS,
} from "@miplatform/constants";
import {
  IRequiredWorkOrder,
  IWorkOrder,
  WorkOrderStatus,
  WorkOrderSubStatus,
} from "@miplatform/types";
import {
  convertTimeToFullShortHand,
  getWorkOrderBorderColor,
} from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

interface MiAbbrWorkOrderCardProps {
  workOrder: IWorkOrder | IRequiredWorkOrder;
}

export const MiAbbrWorkOrderCard: FC<MiAbbrWorkOrderCardProps> = memo(
  ({ workOrder }) => {
    const { status, subStatus, dueDate, completeDate, templateName } =
      useMemo(() => {
        if ((workOrder as IRequiredWorkOrder).workOrderId) {
          const {
            workOrderStatus,
            workOrderName,
            workOrderDueDate,
            workOrderCompleteDate,
            workOrderSubStatus,
          } = workOrder as IRequiredWorkOrder;
          return {
            status: workOrderStatus,
            dueDate: workOrderDueDate,
            subStatus: workOrderSubStatus,
            completeDate: workOrderCompleteDate,
            templateName: workOrderName || "--",
          };
        }
        const {
          status,
          templateName = "--",
          hardDueDate,
          subStatus,
          completeDate,
        } = workOrder as IWorkOrder;

        return {
          status,
          templateName,
          dueDate: hardDueDate,
          subStatus,
          completeDate,
        };
      }, [workOrder]);

    const { classes } = useStyles({
      status,
      subStatus,
      dueDate,
      completeDate,
    });
    const label = WORK_ORDER_STATUSES_LABELS[status];
    const date = useMemo(() => {
      if (completeDate) {
        return `Completed: ${convertTimeToFullShortHand(completeDate)}`;
      }

      if (dueDate) {
        return `Due: ${convertTimeToFullShortHand(dueDate)}`;
      }

      return "--";
    }, [completeDate, dueDate]);

    return (
      <Box className={classes.root}>
        <Typography className={classes.template}>{templateName}</Typography>
        <Typography className={classes.date}>{date}</Typography>
        <Box className={classes.status}>
          {label || WORK_ORDER_STATUSES_LABELS[status]}
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles<{
  status: WorkOrderStatus;
  completeDate?: string;
  dueDate?: string;
  subStatus?: WorkOrderSubStatus;
}>()((theme, { completeDate, dueDate, status, subStatus }) => {
  const { background, text, main } =
    (WORK_ORDER_HOVER_STATUS_MAP[status] as {
      background: string;
      text: string;
      main: string;
    }) || {};
  const statusColorConfig =
    status in WORK_ORDER_ABBR_STATUS_COLOR_MAP
      ? WORK_ORDER_ABBR_STATUS_COLOR_MAP[
          status as keyof typeof WORK_ORDER_ABBR_STATUS_COLOR_MAP
        ]
      : WORK_ORDER_ABBR_STATUS_COLOR_MAP.default;
  const {
    background: statusBackground,
    border: statusBorder,
    status: statusStyles,
  } = statusColorConfig as {
    background: string;
    border: string;
    status?: {
      background: string;
      border: string;
      text?: string;
    };
  };
  const subStatusColor = getWorkOrderBorderColor(subStatus, theme);

  return {
    root: {
      display: "flex",
      backgroundColor: statusBackground,
      border: `1px solid ${subStatusColor || statusBorder}`,
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      gap: theme.spacing(2),
      alignItems: "center",
    },
    template: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.semiBold,
      maxWidth: 100,
    },
    date: {
      fontSize: theme.fontSize.small,
      flex: 1,
      color:
        status === WorkOrderStatus.Complete
          ? theme.palette.grey.medium
          : subStatusColor || theme.palette.grey.medium,
    },
    status: {
      backgroundColor: statusStyles?.background || background,
      border: `0.5px solid ${statusStyles?.border || main}`,
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1),
      fontSize: theme.fontSize.small,
      color: statusStyles?.text || text || main,
    },
  };
});
